//----------------------------------------------------------------------------------------------------------------------
// State Machine
//----------------------------------------------------------------------------------------------------------------------

import type { GameEventBus } from '../classes/eventBus.ts';

//----------------------------------------------------------------------------------------------------------------------

interface TransitionDef
{
    guard ?: () => boolean;
}

//----------------------------------------------------------------------------------------------------------------------

/**
 * A generic finite state machine with optional guard functions, lifecycle callbacks, and event bus integration.
 *
 * @template States - A string union of valid state names.
 */
export class StateMachine<States extends string>
{
    private _currentState : States;
    private readonly transitions = new Map<string, TransitionDef>();
    private readonly wildcardTransitions = new Map<States, TransitionDef>();
    private readonly enterCallbacks = new Map<States, (() => void)[]>();
    private readonly exitCallbacks = new Map<States, (() => void)[]>();
    private readonly eventBus ?: GameEventBus<Record<string, unknown>>;

    constructor(initialState : States, eventBus ?: GameEventBus<Record<string, unknown>>)
    {
        this._currentState = initialState;
        this.eventBus = eventBus;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Properties
    //------------------------------------------------------------------------------------------------------------------

    get currentState() : States
    {
        return this._currentState;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Configuration
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Register a valid transition from one state to another, with an optional guard function.
     */
    addTransition(from : States, to : States, guard ?: () => boolean) : void
    {
        const key = `${ from }->${ to }`;
        this.transitions.set(key, { guard });
    }

    /**
     * Register a wildcard transition: allows transitioning to the given state from any state.
     */
    addTransitionFromAny(to : States, guard ?: () => boolean) : void
    {
        this.wildcardTransitions.set(to, { guard });
    }

    /**
     * Register a callback to run when entering a state.
     */
    onEnter(state : States, callback : () => void) : void
    {
        const callbacks = this.enterCallbacks.get(state) ?? [];
        callbacks.push(callback);
        this.enterCallbacks.set(state, callbacks);
    }

    /**
     * Register a callback to run when exiting a state.
     */
    onExit(state : States, callback : () => void) : void
    {
        const callbacks = this.exitCallbacks.get(state) ?? [];
        callbacks.push(callback);
        this.exitCallbacks.set(state, callbacks);
    }

    //------------------------------------------------------------------------------------------------------------------
    // Queries
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Check if a transition to the given state is defined (ignoring guards).
     */
    canTransition(to : States) : boolean
    {
        const key = `${ this._currentState }->${ to }`;
        return this.transitions.has(key) || this.wildcardTransitions.has(to);
    }

    //------------------------------------------------------------------------------------------------------------------
    // Transitions
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Execute a transition to the target state.
     * Flow: validate -> run guard -> call exit callbacks -> change state -> call enter callbacks.
     *
     * @throws If no transition is defined from the current state to the target, or if a guard rejects.
     */
    transition(to : States) : void
    {
        const key = `${ this._currentState }->${ to }`;
        let transitionDef = this.transitions.get(key);

        // Fall back to wildcard
        if(!transitionDef)
        {
            transitionDef = this.wildcardTransitions.get(to);
        }

        if(!transitionDef)
        {
            throw new Error(
                `No transition defined from '${ this._currentState }' to '${ to }'.`
            );
        }

        // Run guard
        if(transitionDef.guard && !transitionDef.guard())
        {
            throw new Error(
                `Guard rejected transition from '${ this._currentState }' to '${ to }'.`
            );
        }

        const previousState = this._currentState;

        // Exit callbacks
        const exitCbs = this.exitCallbacks.get(previousState);
        if(exitCbs)
        {
            for(const cb of exitCbs)
            {
                cb();
            }
        }

        // Emit exit event -- cast needed because template literal event types can't resolve conditionally
        if(this.eventBus)
        {
            this.eventBus.publish({
                type: `state:exit:${ previousState }`,
                payload: { from: previousState, to },
            } as { type : string; payload : unknown });
        }

        // Change state
        this._currentState = to;

        // Enter callbacks
        const enterCbs = this.enterCallbacks.get(to);
        if(enterCbs)
        {
            for(const cb of enterCbs)
            {
                cb();
            }
        }

        // Emit enter event -- cast needed because template literal event types can't resolve conditionally
        if(this.eventBus)
        {
            this.eventBus.publish({
                type: `state:enter:${ to }`,
                payload: { from: previousState, to },
            } as { type : string; payload : unknown });
        }
    }
}

//----------------------------------------------------------------------------------------------------------------------
